import { useContext } from "react"
import { Navigate, Route, Routes } from "react-router-dom"

import { PageDataParser } from "./PageDataParser"
import { ScrollToTop } from "components/common"
import { ClientContext } from "context/ClientContext"
import { UserContext } from "context/UserContext"
import { env } from "env"
import ConfirmationPage from "pages/ConfirmationPage"
import { FinalCheckPage } from "pages/FinalCheckPage"
import { LogoutPage } from "pages/LogoutPage"
import { DefaultAuthPage } from "pages/susi/DefaultAuthPage"
import { ApplicationData } from "types"
import { AppRoutes, AuthRoutes } from "utilities/constants"

/**
 * A component for generating an application given the applications data in JSON format
 *
 * @param applicationData The JSON representing the application information
 * @returns The component representation of the application data
 */

export const ApplicationDataParser = (applicationData: ApplicationData) => {
  const { pages } = applicationData
  const { user } = useContext(UserContext)
  const { client } = useContext(ClientContext)

  if (!client) {
    throw new Error("Client context is required")
  }

  return (
    <ScrollToTop>
      <Routes>
        <Route
          key={AppRoutes.logout}
          path={AppRoutes.logout}
          element={<LogoutPage />}
        />

        {user?.submittedAt ? (
          // If a user has submitted their app, they can only access /confirmation and /logout
          <>
            <Route
              path={`/${AppRoutes.confirmation}`}
              element={<ConfirmationPage applicationData={applicationData} />}
            />
            <Route
              path="*"
              element={<Navigate to={`/${AppRoutes.confirmation}`} />}
            />
          </>
        ) : (
          // If a user has not submitted their app, they can access all pages except /confirmation
          <>
            {pages.map(pageData => (
              <Route
                key={pageData.slug}
                path={`/${pageData.slug}`}
                element={<PageDataParser pageData={pageData} />}
              />
            ))}
            <Route
              key={AppRoutes.finalCheck}
              path={`/${AppRoutes.finalCheck}`}
              element={<FinalCheckPage applicationData={applicationData} />}
            />
            <Route
              key={AppRoutes.confirmation}
              path={`/${AppRoutes.confirmation}`}
              // If a user with unfinished application hits /confirmation, redirect to /final-check
              element={<Navigate to={`/${AppRoutes.finalCheck}`} />}
            />

            {/* View auth pages for CSS/JS work to copy as HTML to Azure */}
            {env.DEV &&
              AuthRoutes.map(route => {
                return (
                  <Route key={route} path={`/${route}`} element={ 
                    <DefaultAuthPage
                      applicationData={applicationData}
                      pageName={route}
                    />
                  }/>
                )
              })}

            {/* Catch-all route redirects any non-existent URL to Page 1 */}
            <Route path="*" element={<Navigate to={pages[0].slug} replace />} />
          </>
        )}
      </Routes>
    </ScrollToTop>
  )
}
